import React, { useCallback } from "react";
import { CaptionOverlay, CaptionStyles } from "../../../types";
import { CaptionTypographySettings } from "./caption-typography-settings";
import { CaptionColorsSettings } from "./caption-colors-settings";
import { CaptionEffectsSettings } from "./caption-effects-settings";

/**
 * Props for the CaptionStylePanel component
 */
interface CaptionStylePanelProps {
  localOverlay: CaptionOverlay;
  setLocalOverlay: (overlay: CaptionOverlay) => void;
}

/**
 * CaptionStylePanel Component
 *
 * Provides a comprehensive interface for customizing caption styles.
 * Features include:
 * - Typography controls (font, size, weight, spacing, alignment)
 * - Color customization (text, highlight background, highlight text)
 * - Effects settings (scale, shadow, border radius)
 */
export const CaptionStylePanel: React.FC<CaptionStylePanelProps> = ({
  localOverlay,
  setLocalOverlay,
}) => {
  // Ensure styles exist with defaults
  const styles = localOverlay.styles || {
    fontFamily: "Inter, sans-serif",
    fontSize: "2.2rem",
    lineHeight: 1.4,
    textAlign: "center" as const,
    color: "#000000",
  };

  /**
   * Handle changes to top-level style properties
   */
  const handleStyleChange = useCallback(
    (field: keyof CaptionStyles, value: any) => {
      const updatedStyles = { ...styles, [field]: value };
      setLocalOverlay({
        ...localOverlay,
        styles: updatedStyles,
      });
    },
    [localOverlay, styles, setLocalOverlay]
  );

  /**
   * Handle changes to highlight style properties
   */
  const handleHighlightStyleChange = useCallback(
    (field: keyof NonNullable<CaptionStyles["highlightStyle"]>, value: any) => {
      const updatedStyles = {
        ...styles,
        highlightStyle: {
          ...styles.highlightStyle,
          [field]: value,
        },
      };
      setLocalOverlay({
        ...localOverlay,
        styles: updatedStyles,
      });
    },
    [localOverlay, styles, setLocalOverlay]
  );

  return (
    <div className="space-y-4 min-w-0 w-full max-w-full pb-4">
      {/* Typography Settings */}
      <CaptionTypographySettings
        styles={styles}
        onStyleChange={handleStyleChange}
      />

      {/* Colors Settings */}
      <CaptionColorsSettings
        styles={styles}
        onStyleChange={handleStyleChange}
        onHighlightStyleChange={handleHighlightStyleChange}
      />

      {/* Effects Settings */}
      <CaptionEffectsSettings
        styles={styles}
        onStyleChange={handleStyleChange}
        onHighlightStyleChange={handleHighlightStyleChange}
      />
    </div>
  );
};
